import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function Modal({
  isOpen,
  onClose,
  title,
  children
}: ModalProps) {
  return <AnimatePresence data-id="element-28">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 z-50" onClick={onClose} data-id="element-29" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden" data-id="element-30">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8DDD0]" data-id="element-31">
              <h2 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#2D4A3E]" data-id="element-32">
                {title}
              </h2>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-[#F0F5EE] flex items-center justify-center hover:bg-[#E8DDD0] transition-colors" aria-label="Close" data-id="element-33">
                <XIcon className="w-4 h-4 text-[#3A3A3A]" data-id="element-34" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5 font-['Source_Sans_3'] text-[#3A3A3A] leading-relaxed text-sm md:text-base space-y-4" data-id="element-35">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="w-full bg-[#2D4A3E] py-6 px-4" data-id="element-36">
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3" data-id="element-37">
          <p className="font-['Source_Sans_3'] text-sm text-[#8BA888]" data-id="element-38">
            © {new Date().getFullYear()} Peace At Dinner. All rights reserved.
          </p>
          <div className="flex items-center gap-4" data-id="element-39">
            <button onClick={() => setShowPrivacy(true)} className="font-['Source_Sans_3'] text-sm text-[#8BA888] hover:text-white transition-colors underline-offset-2 hover:underline" data-id="element-40">
              Privacy Policy
            </button>
            <span className="text-[#5B8A7A]" data-id="element-41">•</span>
            <button onClick={() => setShowTerms(true)} className="font-['Source_Sans_3'] text-sm text-[#8BA888] hover:text-white transition-colors underline-offset-2 hover:underline" data-id="element-42">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-43">
        <p className="font-semibold text-[#2D4A3E]" data-id="element-44">
          Last updated: February 2026
        </p>

        <p data-id="element-45">
          Your privacy is important to us. This Privacy Policy explains how
          Peace At Dinner ("we," "us," or "our") collects, uses, and protects
          your personal information when you purchase and use our digital
          products.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-46">
          Information We Collect
        </h3>
        <p data-id="element-47">
          When you purchase the Peace At Dinner Protocol, we collect the
          following information:
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-48">
          <li data-id="element-49">Name and email address</li>
          <li data-id="element-50">
            Payment information (processed securely through our third-party
            payment processor)
          </li>
          <li data-id="element-51">
            Device and browser information for delivering your digital product
          </li>
        </ul>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-52">
          How We Use Your Information
        </h3>
        <ul className="list-disc pl-5 space-y-1" data-id="element-53">
          <li data-id="element-54">To deliver your purchased digital product</li>
          <li data-id="element-55">To send order confirmations and product updates</li>
          <li data-id="element-56">To respond to customer support inquiries</li>
          <li data-id="element-57">To improve our products and services</li>
        </ul>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-58">
          Data Protection
        </h3>
        <p data-id="element-59">
          We implement appropriate security measures to protect your personal
          information. We do not sell, trade, or rent your personal information
          to third parties. Payment data is handled exclusively by our secure
          payment processor and is never stored on our servers.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-60">
          Cookies
        </h3>
        <p data-id="element-61">
          Our website may use cookies to enhance your browsing experience. You
          can choose to disable cookies through your browser settings.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-62">
          Your Rights
        </h3>
        <p data-id="element-63">
          You have the right to access, correct, or delete your personal data at
          any time. To exercise these rights, please reach out to us through the
          channels provided at the time of purchase.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-64">
          Changes to This Policy
        </h3>
        <p data-id="element-65">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated revision date.
        </p>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service" data-id="element-66">
        <p className="font-semibold text-[#2D4A3E]" data-id="element-67">
          Last updated: February 2026
        </p>

        <p data-id="element-68">
          By purchasing and using the Peace At Dinner Protocol ("the Product"),
          you agree to the following terms and conditions.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-69">
          Digital Product Delivery
        </h3>
        <p data-id="element-70">
          The Product is a digital download (PDF) delivered instantly upon
          successful payment. You will receive access to download the Product
          immediately after your purchase is confirmed.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-71">
          License & Usage
        </h3>
        <p data-id="element-72">
          Upon purchase, you are granted a personal, non-transferable license to
          use the Product for your own family's benefit. You may not:
        </p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-73">
          <li data-id="element-74">Redistribute, resell, or share the Product with others</li>
          <li data-id="element-75">Reproduce or modify the Product for commercial purposes</li>
          <li data-id="element-76">Claim authorship or ownership of the Product's content</li>
        </ul>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-77">
          Disclaimer
        </h3>
        <p data-id="element-78">
          The Peace At Dinner Protocol is an educational resource based on
          established feeding therapy principles. It is not a substitute for
          professional medical advice, diagnosis, or treatment. If your child
          has a diagnosed medical condition affecting eating, please consult a
          qualified healthcare professional or occupational therapist.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-79">
          Refund Policy
        </h3>
        <p data-id="element-80">
          Due to the digital nature of the Product, all sales are final. If you
          experience technical issues accessing your download, we will work with
          you to resolve the problem promptly.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-81">
          Limitation of Liability
        </h3>
        <p data-id="element-82">
          Peace At Dinner shall not be liable for any indirect, incidental, or
          consequential damages arising from the use of the Product. Results may
          vary based on individual circumstances, consistency of application,
          and each child's unique needs.
        </p>

        <h3 className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg mt-2" data-id="element-83">
          Changes to Terms
        </h3>
        <p data-id="element-84">
          We reserve the right to update these Terms of Service at any time.
          Continued use of the Product constitutes acceptance of any revised
          terms.
        </p>
      </Modal>
    </>;
}